import { Injectable, Signal, TemplateRef, WritableSignal, computed, signal } from '@angular/core';
import { PdfApi } from 'api/pdf/pdf-api.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { DietDb } from 'lib/cosmos/diet/dietDb.js';
import { FoodDb } from 'lib/cosmos/food/foodDb.js';
import { UserDb } from 'lib/cosmos/user/userDb.js';
import { IAzureSearchInput, IAzureSearchResponse } from 'lib/search/search.models.js';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IButton } from '../buttons/index.js';
import { IFileFormFieldCropOptions } from '../form/fields/file-form-field.component.js';
import { SelectItemField } from '../form/fields/select-item-field.component.js';
import { IconType } from '../icons/index.js';
import { ListAvatarMode, ListDesign, ListItem } from '../list/index.js';
import { ExerciseListItem } from '../list/select-exercise-list.component.js';
import { MessageType } from '../messages/index.js';
import { SimpleTableItem } from '../simple-table/index.js';
import { IImage } from '../ui-models.js';

export type DialogSize = 'confirmation' | 'default' | 'large' | 'small' | 'mobile' | 'auto';
export type ConfirmMode = 'delete' | 'default';

const allDialogTypes = [
	'sections',
	'text',
	'default',
	'pdf',
	'confirmation',
	'selection',
	'image',
	'video',
	'form',
	'exercise',
	'foodAmount',
	'azureSearch',
	'images',
	'genai'
] as const;

export type DialogType = (typeof allDialogTypes)[number];

export interface IDialogFooterContext {
	closeDialog(): void;
}

export interface IDialogButtonsContext {
	closeDialog(): void;
}

export interface IDialogSectionContext {
	closeDialog(): void;
}

export interface IDialogSelectionItemContext {
	closeDialog(): void;
}

export type DialogFormFieldType = 'text' | 'textarea' | 'number' | 'dateTime' | 'image';

export interface IDialogMessage {
	readonly title?: string;
	readonly message: string;
	readonly icon?: IconType;
	readonly type: MessageType;
}

export interface IDialogCode {
	readonly code: string | object;
	readonly format: 'json' | 'other';
}

export class DialogBuilder {
	azureSearchDialog<T>(config: DialogConfig.IAzureSearchDialogConfig<T>): IDialog {
		return {
			type: 'azureSearch',
			defaultTitle: 'Search',
			size: 'default',
			shared: config,
			configs: {
				azureSearch: config
			}
		};
	}
	imageDialog(config: DialogConfig.IImageDialogConfig): IDialog {
		return {
			type: 'image',
			defaultTitle: 'Image',
			size: 'default',
			shared: config,
			configs: {
				image: config
			}
		};
	}
	videoDialog(config: DialogConfig.IVideoDialogConfig): IDialog {
		return {
			type: 'video',
			defaultTitle: 'Video',
			size: 'default',
			shared: config,
			configs: {
				video: config
			}
		};
	}
	formDialog<T extends object>(config: DialogConfig.IFormDialogConfig<T>): IDialog {
		return {
			type: 'form',
			defaultTitle: 'Form',
			size: 'default',
			shared: config,
			configs: {
				form: config
			}
		};
	}
	standardDialog(config: DialogConfig.IStandardDialogConfig): IDialog {
		return {
			type: 'default',
			defaultTitle: 'Dialog',
			size: 'default',
			shared: config,
			configs: {
				standard: config
			}
		};
	}
	selectionDialog<T>(config: DialogConfig.ISelectionDialogConfig<T>): IDialog {
		return {
			type: 'selection',
			defaultTitle: 'Search',
			size: config.size ?? 'default',
			shared: config,
			configs: {
				selection: config
			}
		};
	}
	exerciseDialog(config: DialogConfig.IExerciseDialogConfig): IDialog {
		return {
			type: 'exercise',
			defaultTitle: 'Select exercise',
			size: 'large',
			shared: config,
			configs: {
				exercise: config
			}
		};
	}
}

export interface ISectionsDialogConfig {
	readonly dialogId: string;
	readonly sections: IDialogSection[];
	readonly size?: DialogSize;
}

export interface IDialogSection {
	readonly id: string;
	readonly name: string;
	readonly dialog: (builder: DialogBuilder) => IDialog;
}

export interface IDialogFormField<TFormItem extends object> {
	readonly type: DialogFormFieldType;
	readonly title: string | undefined;
	readonly propertyName: CosmosDb.NestedKeyOf<TFormItem>;
	readonly required: boolean;
	readonly enableImageGallery?: boolean;
	readonly maxLength?: number;
	readonly maxValue?: number;
	readonly minValue?: number;
	readonly imageCrop?: IFileFormFieldCropOptions;
	readonly fileUpload?: (file: File) => Observable<CosmosDb.ICosmosFileReference[]>;
	readonly fileRemove?: (file: CosmosDb.ICosmosFileReference) => void;
	readonly fileSelected?: (file: CosmosDb.ICosmosFileReference) => void;
}

export namespace DialogConfig {
	export interface IDialogSharedConfig {
		readonly title?: string;
		readonly text?: string;
		readonly subtitle?: string;
		readonly avatar?: IImage;
		readonly message?: IDialogMessage;
		readonly code?: IDialogCode;
		readonly buttons?: (context: IDialogButtonsContext) => readonly IButton[];
		readonly footerButtons?: (context: IDialogFooterContext) => readonly IButton[];
	}

	export interface IConfirmationDialogConfig extends IDialogSharedConfig {
		readonly title?: string;
		readonly text?: string;
		readonly mode?: ConfirmMode;
		readonly confirm: () => void;
	}

	export interface IImageDialogConfig extends IDialogSharedConfig {
		readonly image: IImage;
		readonly title?: string;
		readonly subtitle?: string;
	}

	export interface IVideoDialogConfig extends IDialogSharedConfig {
		readonly url: string;
		readonly title?: string;
		readonly subtitle?: string;
	}

	export interface IImageDialogConfig extends IDialogSharedConfig {
		readonly image: IImage;
	}

	export interface IImagesDialogConfig extends IDialogSharedConfig {
		readonly images: IImage[];
		readonly onSelect?: (image: IImage) => void;
	}

	export interface ITextDialogConfig extends IDialogSharedConfig {
		size?: DialogSize;
		tableItems?: SimpleTableItem[];
	}

	export interface IAzureSearchDialogConfig<T> extends IDialogSharedConfig {
		search: (data: IAzureSearchInput) => Observable<IAzureSearchResponse<T>>;
		select: (item: T) => void;
		mapSearchResponse: (response: IAzureSearchResponse<T>) => ListItem<any>[];
		defaultSearchTerm?: string;
		pageSize?: number;
	}

	export interface IFoodAmountDialogConfig extends IDialogSharedConfig {
		foodAmount: DietDb.IDietFoodAmount;
		amountMaxValue: number;
		food: FoodDb.IFoodItem;
		portionSelectionItems: SelectItemField<FoodDb.IFoodPortion>[];
		save: (foodAmount: DietDb.IDietFoodAmount) => void;
	}

	export interface IFormDialogConfig<TFormItem extends object> extends IDialogSharedConfig {
		readonly size: DialogSize;
		readonly fields: IDialogFormField<TFormItem>[];
		readonly item: TFormItem;
		readonly hideSaveButton?: boolean;
		readonly save: (item: TFormItem) => void;
	}

	export interface IVideoDialogConfig extends IDialogSharedConfig {
		readonly url: string;
	}

	export interface IPdfDialogConfig extends IDialogSharedConfig {
		readonly generatePdf: () => Observable<PdfApi.GeneratePdfResponse>;
	}

	export interface IGenAiDialogConfig extends IDialogSharedConfig {
		readonly clientContext?: UserDb.IUserItem;
	}

	export interface ISelectionDialogConfig<TItem> extends IDialogSharedConfig {
		readonly items: readonly SelectionDialogItem<TItem>[];
		readonly size?: DialogSize;
		readonly design?: ListDesign;
		readonly title?: string;
		readonly subtitle?: string;
		readonly select: (item: TItem) => void;
		readonly buttons?: (context: IDialogButtonsContext) => readonly IButton[];
		readonly avatarMode?: ListAvatarMode;
	}

	export interface IExerciseDialogConfig extends IDialogSharedConfig {
		readonly items: readonly ExerciseListItem[];
		readonly select: (item: ExerciseListItem) => void;
	}

	export interface IStandardDialogConfig extends IDialogSharedConfig {
		readonly text?: string;
		readonly size: DialogSize;
		readonly template: () => TemplateRef<any> | undefined;
	}
}

export type SelectionDialogItem<TItem> = Omit<ListItem<TItem>, 'buttons'> & {
	readonly buttons?: (context: IDialogSelectionItemContext) => readonly IButton[];
};

export interface IDialogConfigs {
	readonly text?: DialogConfig.ITextDialogConfig;
	readonly image?: DialogConfig.IImageDialogConfig;
	readonly images?: DialogConfig.IImagesDialogConfig;
	readonly form?: DialogConfig.IFormDialogConfig<any>;
	readonly video?: DialogConfig.IVideoDialogConfig;
	readonly pdf?: DialogConfig.IPdfDialogConfig;
	readonly genAi?: DialogConfig.IGenAiDialogConfig;
	readonly foodAmount?: DialogConfig.IFoodAmountDialogConfig;
	readonly azureSearch?: DialogConfig.IAzureSearchDialogConfig<any>;
	readonly selection?: DialogConfig.ISelectionDialogConfig<any>;
	readonly exercise?: DialogConfig.IExerciseDialogConfig;
	readonly standard?: DialogConfig.IStandardDialogConfig;
}

export interface IDialog {
	readonly type: DialogType;
	readonly size: DialogSize;
	readonly shared: DialogConfig.IDialogSharedConfig;
	readonly defaultTitle: string;

	readonly sectionsConfig?: ISectionsDialogConfig;
	readonly configs: IDialogConfigs;
}

@Injectable({
	providedIn: 'root'
})
export class DialogService {
	public readonly dialogChangeEntries: Record<DialogType, BehaviorSubject<IDialog | undefined>> = {
		confirmation: new BehaviorSubject<IDialog | undefined>(undefined),
		default: new BehaviorSubject<IDialog | undefined>(undefined),
		image: new BehaviorSubject<IDialog | undefined>(undefined),
		azureSearch: new BehaviorSubject<IDialog | undefined>(undefined),
		foodAmount: new BehaviorSubject<IDialog | undefined>(undefined),
		form: new BehaviorSubject<IDialog | undefined>(undefined),
		pdf: new BehaviorSubject<IDialog | undefined>(undefined),
		sections: new BehaviorSubject<IDialog | undefined>(undefined),
		selection: new BehaviorSubject<IDialog | undefined>(undefined),
		text: new BehaviorSubject<IDialog | undefined>(undefined),
		video: new BehaviorSubject<IDialog | undefined>(undefined),
		images: new BehaviorSubject<IDialog | undefined>(undefined),
		genai: new BehaviorSubject<IDialog | undefined>(undefined),
		exercise: new BehaviorSubject<IDialog | undefined>(undefined)
	};

	public readonly dialogCloseEntries: Record<DialogType, Subject<void>> = {
		confirmation: new Subject<void>(),
		default: new Subject<void>(),
		image: new Subject<void>(),
		azureSearch: new Subject<void>(),
		foodAmount: new Subject<void>(),
		form: new Subject<void>(),
		pdf: new Subject<void>(),
		sections: new Subject<void>(),
		selection: new Subject<void>(),
		text: new Subject<void>(),
		video: new Subject<void>(),
		images: new Subject<void>(),
		genai: new Subject<void>(),
		exercise: new Subject<void>()
	};

	public readonly openedDialogs: WritableSignal<DialogType[]> = signal<DialogType[]>([]);

	public readonly openedDialogsCount: Signal<number> = computed(() => {
		return this.openedDialogs().length;
	});

	getAllDialogTypes(): DialogType[] {
		const dialogTypes: DialogType[] = [];

		for (const dialogType of allDialogTypes) {
			dialogTypes.push(dialogType);
		}

		return dialogTypes;
	}

	openTextDialog(data: DialogConfig.ITextDialogConfig): void {
		this.dialogChangeEntries['text'].next({
			type: 'text',
			shared: data,
			size: data.size ?? 'default',
			defaultTitle: '',
			configs: {
				text: data
			}
		});
	}

	openFormDialog<TFormItem extends object>(data: DialogConfig.IFormDialogConfig<TFormItem>): void {
		this.dialogChangeEntries['form'].next({
			type: 'form',
			shared: data,
			size: data.size,
			defaultTitle: 'Form',
			configs: {
				form: data
			}
		});
	}

	openFoodAmountDialog(data: DialogConfig.IFoodAmountDialogConfig): void {
		this.dialogChangeEntries['foodAmount'].next({
			type: 'foodAmount',
			shared: data,
			size: 'auto',
			defaultTitle: 'Set amount',
			configs: {
				foodAmount: data
			}
		});
	}

	openDialogWithSections(data: ISectionsDialogConfig): void {
		this.dialogChangeEntries['default'].next({
			type: 'sections',
			shared: {},
			size: data.size ?? 'default',
			sectionsConfig: data,
			defaultTitle: '',
			configs: {}
		});
	}

	openAzureSearchDialog<T>(data: DialogConfig.IAzureSearchDialogConfig<T>): void {
		this.dialogChangeEntries['azureSearch'].next({
			type: 'azureSearch',
			shared: data,
			size: 'default',
			defaultTitle: 'Search',
			configs: {
				azureSearch: data
			}
		});
	}

	openImageDialog(data: DialogConfig.IImageDialogConfig): void {
		this.dialogChangeEntries['image'].next({
			type: 'image',
			shared: data,
			defaultTitle: 'Image',
			size: 'large',
			configs: {
				image: data
			}
		});
	}

	openImagesDialog(data: DialogConfig.IImagesDialogConfig): void {
		this.dialogChangeEntries['images'].next({
			type: 'images',
			shared: data,
			defaultTitle: 'Images',
			size: 'large',
			configs: {
				images: data
			}
		});
	}

	openVideoDialog(data: DialogConfig.IVideoDialogConfig): void {
		this.dialogChangeEntries['video'].next({
			type: 'video',
			defaultTitle: 'Video',
			size: 'default',
			shared: data,
			configs: {
				video: data
			}
		});
	}

	openPdfDialog(data: DialogConfig.IPdfDialogConfig): void {
		this.dialogChangeEntries['pdf'].next({
			type: 'pdf',
			defaultTitle: 'Pdf',
			size: 'small',
			shared: data,
			configs: {
				pdf: data
			}
		});
	}

	openGenAiDialog(data: DialogConfig.IGenAiDialogConfig): void {
		this.dialogChangeEntries['genai'].next({
			type: 'genai',
			defaultTitle: 'Ask AI',
			size: 'large',
			shared: data,
			configs: {
				genAi: data
			}
		});
	}

	openConfirmationDialog(data: DialogConfig.IConfirmationDialogConfig): void {
		const defaultText: string =
			data.mode === 'delete' ? `Are you sure to delete the item?` : `Are you sure to continue?`;

		this.dialogChangeEntries['confirmation'].next({
			type: 'confirmation',
			shared: {
				...data,
				title: data.title ?? 'Confirmation',
				text: data.text ?? defaultText,
				footerButtons: (context) => {
					const customButtons = data.buttons?.(context) ?? [];
					return [
						...customButtons,
						{
							buttonType: 'basic',
							title: `Cancel`,
							buttonSize: 'default',
							click: () => context.closeDialog()
						},
						{
							buttonType: data.mode === 'delete' ? 'delete' : 'primary',
							title: data.mode === 'delete' ? 'Delete' : 'Confirm',
							buttonSize: 'default',
							click: () => {
								context.closeDialog();
								data.confirm();
							}
						}
					];
				}
			},
			defaultTitle: 'Confirmation',
			size: 'confirmation',
			configs: {}
		});
	}

	openSelectionDialog<TItem>(data: DialogConfig.ISelectionDialogConfig<TItem>): void {
		this.dialogChangeEntries['confirmation'].next({
			type: 'selection',
			shared: data,
			defaultTitle: `Select item`,
			size: data.size ?? 'default',
			configs: {
				selection: data
			}
		});
	}

	openExerciseDialog(data: DialogConfig.IExerciseDialogConfig): void {
		this.dialogChangeEntries['exercise'].next({
			type: 'exercise',
			shared: data,
			defaultTitle: `Select exercise`,
			size: 'large',
			configs: {
				exercise: data
			}
		});
	}

	openDialog(data: DialogConfig.IStandardDialogConfig): void {
		this.dialogChangeEntries['default'].next({
			type: 'default',
			defaultTitle: 'Dialog',
			size: data.size,
			shared: data,
			configs: {
				standard: data
			}
		});
	}

	closeDialog(dialogRole: DialogType): void {
		this.dialogCloseEntries[dialogRole].next();
	}

	closeAllDialogs(): void {
		for (const [key, dialog] of Object.entries(this.dialogCloseEntries)) {
			dialog.next();
		}
	}
}
